import axios, { AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { GenerateCurrentEpochSeconds } from '../utils/GeneralUtils';
import { JobberStatus, UserDetails } from '../common/CommonInterfaces';
import { showGlobalError } from '../shared-components/ErrorContextProvider';
import { setAccessToken, setRefreshToken, setSessionStorageValue, toStringSafely, LOCAL_STORAGE_KEYS, getRefreshToken, getAccessToken, getAuthTokenExpireDateTimeEpoch, getSessionStorageValue, setAuthTokenExpireDateTimeEpoch, handleSignOut, isValidString, getAuthFlow } from '../common/CommonUtilsMethods';
import { AUTH_FLOW_TYPE } from '../auth/WorkyAuthClientConfigInterfaces';
import { ReceiptRussianRuble, Trophy } from 'lucide-react';
const workyServerIDPSIGNINURL = process.env.REACT_APP_IDP_SIGN_IN_URL;
//TODO NEED TO MODIFY REFRESH TOKE URL AS IT IS FAILING
const workyServerREFRESHTOKENAPIURL = process.env.REACT_APP_BACK_END_SERVER_REFRESH_URI_LOCAL;
const BACK_END_SERVER_BASE_PATH_LOCAL = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL;
const workyServerAPIURL = process.env.REACT_APP_BACK_END_SERVER_SIGN_IN_URI_LOCAL;
const redirectURL = process.env.REACT_APP_BACK_END_SERVER_SIGN_IN_REDIRECT_URL;
// import { WorkyServiceResponseStatus, ServerErrorDetails } from './RemoteExeConsts.ts';

/**
 *
 *
 * Interfaces Defined
 *
 *
 *
 */



/**
 * Defines the input obj for the Get call
 * Ensure that the URL is just the base url w/ out hardcoded query params
 */
interface RemoteGETCallInputObject {
	apiUrl: string;
	queryParams: Record<string, string>;
	headers: Record<string, string>;
}
/**
 * Defines the input obj for the POST call
 * The variable N defines the type of input object that is sent in the request
 */
interface RemotePOSTCallInputObject<N> {
	apiUrl: string;
	headers: Record<string, string>;
	object: N;
}

enum WorkyServiceResponseStatus {
	SUCCESS = 'SUCCESS',
	RECOVERABLE = 'RECOVERABLE',
	UNRECOVERABLE = 'UNRECOVERABLE',
	UNDEFINED = 'UNDEFINED',
}

interface ServerErrorDetails<E> {
	message: string;
	status: WorkyServiceResponseStatus | null;
	clazz: new (...args: any[]) => E;
}

interface ServerResponse<T, E> {
	data: T | null;
	errorDetails: ServerErrorDetails<E> | null;
}

/**
 * Defines the input obj for the POST call
 * The variable N defines the type of input object that is sent in the request
 */
interface RemotePOSTCallInputObject<N> {
	apiUrl: string;
	headers: Record<string, string>;
	object: N;
}

interface RemoteExecutionObject {
	remoteGETCallV2: <T>(getCallInput: RemoteGETCallInputObject) => Promise<AxiosResponse<T>>;
	remotePOSTCallV2: <T, N>(
		postCallInput: RemotePOSTCallInputObject<N>
	) => Promise<AxiosResponse<T>>;
}

/**
 *
 *
 * Interfaces Defined
 *
 *
 *
 */

/**
 *
 *
 * Globally used API defined
 *
 *
 */
const backendApiBasePathLocal = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL;
const api = axios.create({
	baseURL: backendApiBasePathLocal,
	headers: {
		'Content-Type': 'application/json',
		'Access-Control-Allow-Origin': '*',
		// other default headers
	},
});

const redirectToLogin = (): void => {
	console.log('Redirecting ');
	void import('../auth/WorkyAuthNavigationService').then((authNavigationModule) => {
		void authNavigationModule.redirectToLogin();
	});
};

type WorkyAxiosRequestConfig = InternalAxiosRequestConfig & { __workyAuthRetry?: boolean };

/**
 *
 *
 * Globally used API defined
 *
 *
 */

// Add an Axios interceptor to include the access token in the request headers

let refreshInFlightPromise: Promise<boolean> | null = null;

const shouldSkipAuthRetryForUrl = (requestPath: string | undefined): boolean => {
	if (!isValidString(requestPath)) {
		return false;
	}
	const path: string = requestPath as string;
	if (path.includes('/auth')) {
		return true;
	}
	const signInSegment: string = workyServerAPIURL ?? '';
	if (isValidString(signInSegment) && path.includes(signInSegment.replace(/^\//, ''))) {
		return true;
	}
	const refreshSegment: string = workyServerREFRESHTOKENAPIURL ?? '';
	if (isValidString(refreshSegment) && path.includes(refreshSegment.replace(/^\//, ''))) {
		return true;
	}
	return false;
};

const refreshAccessTokenSingleFlight = async (inputRefreshToken?: string | null): Promise<boolean> => {
	if (refreshInFlightPromise !== null) {
		return await refreshInFlightPromise;
	}
	refreshInFlightPromise = (async (): Promise<boolean> => {
		try {
			const refreshTokenValue: string | null = isValidString(inputRefreshToken)
				? (inputRefreshToken as string)
				: getRefreshToken();
			if (!refreshTokenValue || !isValidString(refreshTokenValue)) {
				console.log('refreshAccessTokenSingleFlight: no refresh token');
				handleSignOut();
				return false;
			}
			const resp: AxiosResponse<ServerResponse<WorkyAuthTokenResponse, Error>, unknown> | null =
				await AuthGetCallRefreshToken({
					refreshToken: refreshTokenValue,
				});
			if (!resp?.data?.data) {
				console.log('refreshAccessTokenSingleFlight: unable to retrieve new tokens', resp);
				handleSignOut();
				return false;
			}
			const tokenPayload = resp.data.data;
			UpdateLocalCacheWOAuthInfo({
				basicDetails: tokenPayload.basicJobberDetails,
				input: tokenPayload,
			});
			return true;
		} catch (e: unknown) {
			console.log('refreshAccessTokenSingleFlight failed:', e);
			handleSignOut();
			return false;
		} finally {
			refreshInFlightPromise = null;
		}
	})();
	return await refreshInFlightPromise;
};

const handleRetrieveNewTokens = async (inputRefreshToken: string): Promise<boolean> => {
	const token: string | null =
		inputRefreshToken === null || inputRefreshToken === undefined ? getRefreshToken() : inputRefreshToken;
	console.log('handleRetrieveNewTokens called');
	return await refreshAccessTokenSingleFlight(token);
};

api.interceptors.request.use(async (config) => {
	const requestPath = config.url;
	// Corresponds to careers.scoutsecond.server.config.AuthConfig
	if (requestPath && requestPath.includes('/auth')) {
		console.log('Call to unsecured backend API, not appending tokens: ', requestPath);
		return config;
	}
	// Replace 'your_access_token' with the actual access token or retrieve it from your state management
	const accessToken = getAccessToken();
	const expireTime = getAuthTokenExpireDateTimeEpoch()?.toString();
	const refreshToken = getRefreshToken();
	const isExpired = isExpiredCalc(expireTime);
	if (accessToken && !isExpired) {
		// console.log(' access token present in inteceptor');
		config.headers.Authorization = `Bearer ${accessToken}`;
	} else if (accessToken && isExpired) {
		const refreshed: boolean = await refreshAccessTokenSingleFlight(refreshToken);
		if (refreshed) {
			const newAccessToken: string | undefined = getAccessToken();
			if (isValidString(newAccessToken)) {
				config.headers.Authorization = `Bearer ${newAccessToken}`;
			}
		} else {
			redirectToLogin();
		}
	} else {
		// window.location.assign(workyServerIDPSIGNINURL);
	}

	return config;
});

api.interceptors.response.use(
	(response: AxiosResponse) => response,
	async (error: unknown): Promise<AxiosResponse> => {
		if (!isAxiosError(error) || !error.response || error.response.status !== 401) {
			return Promise.reject(error);
		}
		const originalRequest: WorkyAxiosRequestConfig | undefined = error.config as
			| WorkyAxiosRequestConfig
			| undefined;
		if (!originalRequest) {
			return Promise.reject(error);
		}
		const requestPath: string | undefined = originalRequest.url;
		if (shouldSkipAuthRetryForUrl(requestPath)) {
			return Promise.reject(error);
		}
		if (originalRequest.__workyAuthRetry === true) {
			return Promise.reject(error);
		}
		originalRequest.__workyAuthRetry = true;
		const refreshed: boolean = await refreshAccessTokenSingleFlight();
		if (!refreshed) {
			return Promise.reject(error);
		}
		return api.request(originalRequest);
	}
);

/**
 * This method is how Worky makes remote GET calls
 * The parameter of T defines what the expected return obj is of this API call
 * The method returns a Promise obj of type AxiosResponse which has the return data of type T
 * @param getCallInput is the input GET obj
 * @returns the expected return obj of Type T
 */
const remoteGETCallV2 = async <T>(
	getCallInput: RemoteGETCallInputObject
): Promise<AxiosResponse<T>> => {
	try {
		// console.log(`Attempt of remote GETCallV2: ${getCallInput.apiUrl} `);
		const response: AxiosResponse<T> = await api.get(getCallInput.apiUrl, {
			params: getCallInput.queryParams,
			headers: getCallInput.headers,
		});
		// console.log(
		// 	`Result of remote GETCallV2: ${
		// 		getCallInput.apiUrl
		// 	} w/ typeof: ${typeof response.data} result`,
		// 	response.data
		// );
		// return {data: response.data, status: response.status};
		return response;
	} catch (error) {
		console.log(
			`Error while making remote get call to: ${getCallInput.apiUrl} w/ error:`,
			error
		);

		// Check if this is an external/analytics API
		const isExternalOrAnalyticsAPI = getCallInput?.apiUrl?.includes('ipinfo') || 
											getCallInput?.apiUrl?.includes('sales-tracker') ||											  
											getCallInput?.apiUrl?.includes('telemetry');

		// Handle 403 Forbidden errors from external APIs (especially ipinfo.io) gracefully
		if (error?.response && error?.response?.status === 403 && isExternalOrAnalyticsAPI) {
			console.log(`403 Forbidden from external API (likely rate limit or invalid token): ${getCallInput.apiUrl}`);
			// Throw a specific error that won't trigger the error boundary
			// This allows the calling code to catch and handle it gracefully
			const suppressedError = new Error(`External API returned 403: ${getCallInput.apiUrl}`);
			(suppressedError as any).isSuppressed = true;
			(suppressedError as any).originalError = error;
			throw suppressedError;
		}

		// Don't trigger global error for network errors on external/analytics APIs
		if(isExternalOrAnalyticsAPI){
			console.log("Skipping global error for network errors on external/analytics APIs", getCallInput.apiUrl);
			// For external APIs, throw a suppressed error that can be caught gracefully
			const suppressedError = new Error(`External API error: ${getCallInput.apiUrl}`);
			(suppressedError as any).isSuppressed = true;
			(suppressedError as any).originalError = error;
			throw suppressedError;
		}

		// Check if this is an aborted request - don't show global error for abort errors
		if (isAbortedHTTPRequestError(error)) {
			console.log(`Request to ${getCallInput.apiUrl} was aborted, not showing global error`);
			throw error;
		}

		showGlobalError(error, getCallInput.apiUrl, toStringSafely(getCallInput.queryParams));

		throw error;
	}
};

/**
 * This method is how Worky makes remote POST calls
 * It accepts 2 argument types of T and N
 * T is the expected return type
 * N is the expected input object type(the POST.body.data type)
 * @param postCallInput default post call obj
 * @returns the expected return obj of Type T
 */
const remotePOSTCallV2 = async <T, N>(
	postCallInput: RemotePOSTCallInputObject<N>
): Promise<AxiosResponse<T>> => {
	try {
		const response: AxiosResponse<T> = await api.post(
			postCallInput.apiUrl,
			postCallInput.object,
			{
				headers: postCallInput.headers,
			}
		);
		// console.log(
		// 	`Result of remote remotePOSTCallV2 : ${postCallInput.apiUrl} w/ result`,
		// 	response.data
		// );
		return response;
	} catch (error) {
		console.log(
			`Error while making post get call to: ${postCallInput.apiUrl} w/ error:`,
			error
		);
		// Telemetry / error-reporting POSTs must never re-trigger the global error page.
		// Every caller already catches and handles their failure.
		const isTelemetryPostCall = postCallInput?.apiUrl?.includes('telemetry') ||
										postCallInput?.apiUrl?.includes('sales-tracker');
		if (isTelemetryPostCall) {
			console.log("Skipping global error for failed telemetry POST", postCallInput.apiUrl);
			throw error;
		}

		showGlobalError(error, postCallInput.apiUrl, toStringSafely(postCallInput.object))
		throw error;
	}
};

const RemoteExecutionServiceImpl = (): RemoteExecutionObject => {
	const callRemoteGetCallV2Helper = async <T>(
		getCallInput: RemoteGETCallInputObject
	): Promise<AxiosResponse<T>> => {
		return remoteGETCallV2<T>(getCallInput);
	};

	const callRemotePOSTCALLV2Helper = async <T, N>(
		postCallInput: RemotePOSTCallInputObject<N>
	): Promise<AxiosResponse<T>> => {
		return remotePOSTCallV2(postCallInput);
	};
	return {
		remoteGETCallV2: callRemoteGetCallV2Helper,
		remotePOSTCallV2: callRemotePOSTCALLV2Helper,
	};
};

const remotePOSTCallV2Unsecured = async <T, N>(
	postCallInput: RemotePOSTCallInputObject<N>
): Promise<AxiosResponse<T>> => {
	try {
		console.log(
			`Making remotePOSTCallV2 to: ${postCallInput.apiUrl} w/ data: `,
			postCallInput.object
		);
		const response: AxiosResponse<T> = await axios.post(
			BACK_END_SERVER_BASE_PATH_LOCAL + postCallInput.apiUrl,
			postCallInput.object,
			{
				headers: postCallInput.headers,
			}
		);
		console.log(
			`Result of remote remotePOSTCallV2 : ${postCallInput.apiUrl} w/ result`,
			response.data
		);
		return response;
	} catch (error) {
		// Check if the error status code is unauthorized (HTTP status code 401)
		if (error.response && error.response.status === 401) {
			// Redirect the user to the login page
			console.log(
				'During post: {} User is not signed in redirecting to relogin',
				postCallInput
			);
			// window.location.assign(workyServerIDPSIGNINURL);
			handleSignOut();			
			return;
		} else if(isAbortedHTTPRequestError(error)) {
			console.log(`For ${postCallInput.apiUrl} error is aborted, will not handle sign out`, error);
			return;
		} else{
			console.log(`For ${postCallInput.apiUrl} error is not aborted, will handle sign out`, error);
			handleSignOut();		
			throw error;
		}
		
	}
};

// export { ServerResponse, ServerErrorDetails };

/***
 *
 * Sign in stuff
 *
 *
 *
 */

interface userEmail {
	value: string;
}
interface PhoneNumber {
	value: string;
}

interface WorkyAuthTokenRequest {
	authCode: string;
	redirectURI: string;
	fullUri?: string;
}

interface WorkyAuthRefreshRequest {
	refreshToken: string;
	isQA: boolean;
	useCustomAuthSpaClient: boolean;
}

interface WorkySignInResponse {
	tokens: WorkyAuthTokenResponse;
	basicJobberDetails: BasicJobberDetails;
}

interface BasicJobberDetails {
	userDetails: UserDetails;
	jobberStatus?: JobberStatus;
	userType: UserType;
}



enum UserType {
	JOBBER = 'JOBBER',
	ORG_MANAGER = 'ORG_MANAGER',
	DIRECT_CARE_PATIENT = 'DIRECT_CARE_PATIENT',
	DIRECT_KIN_CARE_GIVER = 'DIRECT_KIN_CARE_GIVER',
	AGENCY_STAFF = "AGENCY_STAFF",
	AGENCY_STAFF_SCOPED = "AGENCY_STAFF_SCOPED",
	AGENCY_ADMIN = "AGENCY_ADMIN",
	WORKY_STAFF = 'WORKY_STAFF',
	WORKY_ADMIN = 'WORKY_ADMIN',
}

interface WorkyAuthTokenResponse {
	accessToken: string;
	idToken: string;
	refreshToken: string;
	useremail: userEmail;
	expiresInMilliseconds: number;
	basicJobberDetails: BasicJobberDetails;
}

const isExpiredCalc = (expireTime: string): boolean => {
	if (!expireTime) return false;
	const expireTimeValue = parseInt(expireTime);
	if (!expireTimeValue || isNaN(expireTimeValue)) return false;
	const currentEpochSeconds = GenerateCurrentEpochSeconds();
	if (expireTimeValue < currentEpochSeconds) {
		return true;
	} else {
		// console.log(
		// 	'access token is valid expireTime: ' +
		// 		expireTime +
		// 		' currentEpochSeconds: ' +
		// 		currentEpochSeconds
		// );
		return false;
	}
};

const UpdateLocalCacheWOAuthInfo = ({
	basicDetails,
	input,
}: {
	basicDetails?: BasicJobberDetails;
	input: WorkyAuthTokenResponse;
}) => {
	if (!input) {
		console.error('given token info is null, cannot update localStorage', input);
	}

	const accessToken = input.accessToken;
	const refreshToken = input.refreshToken;
	const expireTime = input.expiresInMilliseconds;
	
	setSessionStorageValue({key: LOCAL_STORAGE_KEYS.ACCESS_TOKEN, value: accessToken});

	if (refreshToken) {		
		setRefreshToken(refreshToken);

	}
	if (expireTime) {		
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.EXPIRE_TIME, value: expireTime.toString()});
		setAuthTokenExpireDateTimeEpoch(expireTime);
	}

	if (basicDetails && basicDetails.userType) {
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_TYPE, value: basicDetails.userType});
	}
	if(basicDetails && basicDetails.userDetails && basicDetails.userDetails.userEmail) {
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_EMAIL, value: basicDetails.userDetails.userEmail.value});
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_ID, value: basicDetails.userDetails.userId});
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.FIRST_NAME, value: basicDetails.userDetails.firstName});
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.LAST_NAME, value: basicDetails.userDetails.lastName});
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_PHONE_NUMBER, value: basicDetails.userDetails.userPhoneNumber.value});

	}

};

const remoteCallObject = RemoteExecutionServiceImpl();

const AuthGetCall = async ({
	authCodeInput,
	redirectURIInput,
}: {
	authCodeInput: string;
	redirectURIInput: string;
}): Promise<AxiosResponse<ServerResponse<WorkySignInResponse, Error>> | null> => {
	try {
		const tempResp: WorkyAuthTokenRequest = {
			authCode: authCodeInput,
			redirectURI: redirectURIInput,
			fullUri: window.location.href
		};

		const response: AxiosResponse<ServerResponse<WorkySignInResponse, Error>> =
			await remoteCallObject.remotePOSTCallV2<
				ServerResponse<WorkySignInResponse, Error>,
				WorkyAuthTokenRequest
			>({
				apiUrl: workyServerAPIURL,
				// apiUrl: '/tst',
				object: tempResp,
				headers: { 'Content-Type': 'application/json' },
			});

		return response;
	} catch (e) {
		redirectToLogin();
		console.error('POST request failed:', e);
		return null;
	}
};

const determineIfItIsQA = (url) => {
	if (redirectURL === null || redirectURL === undefined || redirectURL.includes('localhost')) {
		console.log('Redirect URL is null, undefined, or includes "localhost"');
		return true;
	} else {
		return false;
	}
};

const readUseCustomAuthSpaClientForRefresh = (): boolean => {
	const authFlow: string | null = getAuthFlow();
	if (authFlow === AUTH_FLOW_TYPE.CUSTOM) {
		return true;
	}
	if (authFlow === AUTH_FLOW_TYPE.HOSTED) {
		return false;
	}

	const spaClientId: string = process.env.REACT_APP_COGNITO_SPA_CLIENT_ID ?? '';
	const idToken: string = getSessionStorageValue({ key: LOCAL_STORAGE_KEYS.ID_TOKEN });
	if (!isValidString(spaClientId) || !isValidString(idToken)) {
		return false;
	}

	try {
		const tokenPayloadJson: string = atob(idToken.split('.')[1]);
		const tokenPayload: { aud?: string | readonly string[] } = JSON.parse(tokenPayloadJson);
		const tokenAudience: string | readonly string[] | undefined = tokenPayload.aud;
		if (Array.isArray(tokenAudience)) {
			return tokenAudience.includes(spaClientId);
		}
		return tokenAudience === spaClientId;
	} catch (decodeError: unknown) {
		console.log('Unable to infer custom auth flow from id token audience', decodeError);
		return false;
	}
};

const AuthGetCallRefreshToken = async ({
	refreshToken,
}: {
	refreshToken: string;
}): Promise<AxiosResponse<ServerResponse<WorkyAuthTokenResponse, Error>> | null> => {
	if (!refreshToken) {
		console.log('Refresh token is null, will not make call ', refreshToken);
		return null;
	}

	const isQA: boolean = determineIfItIsQA(redirectURL);
	const useCustomAuthSpaClient: boolean = readUseCustomAuthSpaClientForRefresh();

	try {
		const req: WorkyAuthRefreshRequest = {
			refreshToken: refreshToken,
			isQA: isQA,
			useCustomAuthSpaClient: useCustomAuthSpaClient,
		};

		const response: AxiosResponse<ServerResponse<WorkyAuthTokenResponse, Error>> | undefined =
			await remotePOSTCallV2Unsecured<
				ServerResponse<WorkyAuthTokenResponse, Error>,
				WorkyAuthRefreshRequest
			>({
				apiUrl: workyServerREFRESHTOKENAPIURL,
				object: req,
				headers: { 'Content-Type': 'application/json' },
			});

		if (response === undefined || response.status !== 202 || !response.data?.data) {
			console.log('Refresh token request failed or returned no tokens', response?.data);
			handleSignOut();
			return null;
		}

		console.log(' for refresh token POST request successful:', response);
		return response;
	} catch (e) {
		console.log('Error unable to use refresh token to get new tokens due to: ', e);
		redirectToLogin();
		return null;
	}
};

const setGlobalUserDetails = ({ userInfo }: { userInfo: UserDetails }) => {
	if (!userInfo) return;

	setSessionStorageValue({key: LOCAL_STORAGE_KEYS.FIRST_NAME, value: userInfo.firstName});
	setSessionStorageValue({key: LOCAL_STORAGE_KEYS.LAST_NAME, value: userInfo.lastName});
	setSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_ID, value: userInfo.userId});

	if (userInfo.userEmail) {
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_EMAIL, value: userInfo.userEmail.value});
	}

	if (userInfo.userPhoneNumber) {
		setSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_PHONE_NUMBER, value: userInfo.userPhoneNumber.value});
	}
};

const getGlobalUserDetails = (): UserDetails => {
	const firstName = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.FIRST_NAME});
	const lastName = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.LAST_NAME});
	const userId = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_ID});
	const userEmail = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_EMAIL});
	const userPhoneNumber = getSessionStorageValue({key: LOCAL_STORAGE_KEYS.USER_PHONE_NUMBER});

	return {
		firstName: firstName,
		userId: userId,
		lastName: lastName,
		userEmail: { value: userEmail },
		userPhoneNumber: { value: userPhoneNumber },
	};
};

const isAbortedHTTPRequestError = (error: any) => {
	try{
		if(!error) return false;
		if(error.message && error.message.toLowerCase().includes('aborted')) return true;
		if(error.code && error.code === 'ECONNABORTED') return true;
		if(error.response && error.response.message && error.response.message.toLowerCase().includes('aborted')) return true;
		
		return false;
	}catch(e){
		console.log("error in isAbortedHTTPRequestError", e);
		return false;
	}
}

// export { ServerResponse,RemoteExecutionServiceImpl, ServerErrorDetails };
export {
	UserType,
	ServerResponse,
	RemoteExecutionServiceImpl,
	ServerErrorDetails,
	AuthGetCall,
	WorkySignInResponse,
	WorkyAuthRefreshRequest,
	isExpiredCalc,
	UpdateLocalCacheWOAuthInfo,
	RemoteGETCallInputObject,
	redirectToLogin,
	handleRetrieveNewTokens,	
	setGlobalUserDetails,
	getGlobalUserDetails,
	WorkyAuthTokenResponse,
	BasicJobberDetails,
	RemotePOSTCallInputObject
};
	

